<template>
    <DefaultTemplate :model="model">
        <Container>
            <div class="container">
                <div class="mt-12 mb-8">
                    <Breadcrumbs :breadcrumbs="model.breadcrumbs" className="text-grey-400" />
                </div>
            </div>

            <!-- Name -->
            <div class="container bg-white mb-1 p-4">
                <h1 v-epi-edit="'Name'" class="text-2xl font-bold m-0 p-0">
                    {{ model.name }}
                </h1>
            </div>
            <!-- // Name -->

            <!-- Meta -->
            <div class="container bg-white mb-1 p-4 md:flex divide-y md:divide-y-0 md:divide-x text-sm">
                <div class="hidden md:block w-full md:w-1/12 p-4 pl-0 text-center">
                    <img :src="model.fylkesvaapenImage"
                         alt="Fylkesvåpen"
                         class="my-4 inline-block" />
                </div>
                <div class="px-0 py-4 md:p-4 flex-1">
                    <ul>
                        <li class="mb-1">
                            Arena:
                            <strong v-for="(arena, index) in arenas" :key="index">
                                {{arena}},
                            </strong>
                            <div v-show="inEditMode">
                                <EpiProperty propertyName="ArenaIdLst" displayText="Arena" />
                            </div>
                        </li>
                        <li class="mb-1">
                            Målgruppe:
                            <strong v-for="(targetgroup, index) in targetgroups" :key="index">
                                {{targetgroup}},
                            </strong>
                            <div v-show="inEditMode">
                                <EpiProperty propertyName="MalgruppeIdLst" displayText="Målgruppe" />
                            </div>
                        </li>
                        <li class="mb-1">
                            Tema:
                            <strong v-for="(theme, index) in themes" :key="index">
                                {{theme}},
                            </strong>
                            <div v-show="inEditMode">
                                <EpiProperty propertyName="TemaIdLst" displayText="Tema" />
                            </div>
                        </li>
                    </ul>
                </div>
                <div class="px-0 py-4 md:p-4 flex-1">
                    <ul>
                        <li v-if="model.aarOppstart || model.aarAvslutt || inEditMode" class="mb-1 flex flex-row">
                            <template v-if="model.aarOppstart && model.aarAvslutt">
                                Varighet:&nbsp;
                            </template>
                            <template v-else-if="model.aarOppstart && !model.aarAvslutt">
                                Oppstart:&nbsp;
                            </template>
                            <template v-else-if="!model.aarOppstart && model.aarAvslutt">
                                Avsluttning:&nbsp;
                            </template>
                            <div class="flex flex-row">
                                <div v-epi-edit="'AarOppstart'"><strong>{{ model.aarOppstart }}</strong></div>
                                &dash;
                                <div v-epi-edit="'AarAvslutt'"><strong>{{ model.aarAvslutt }}</strong></div>
                            </div>

                        </li>
                        <li>
                            <template v-if="model.isClosed">
                                Status:
                                <strong>
                                    Avsluttet
                                </strong>
                            </template>
                        </li>
                        <li v-epi-edit="'KontaktNavn'" v-if="model.kontaktNavn || inEditMode" class="mb-1">
                            Kontaktpersoner:
                            <ul v-if="model.kontakter">
                                <li v-for="kontakt in model.kontakter" :key="kontakt.name">
                                    <strong>{{ kontakt.name }}</strong>
                                </li>
                            </ul>
                        </li>
                    </ul>
                </div>
                <div class="px-0 py-4 md:p-4 flex-1">
                    <ul>
                        <li v-epi-edit="'Kommune'"  class="mb-1">
                            Kommune:
                            <strong>
                                {{ model.kommune }}
                            </strong>
                        </li>
                        <li class="mb-1">
                            Fylke:
                            <strong>
                                {{ model.fylke }}
                            </strong>
                            <div v-show="inEditMode">
                                <EpiProperty propertyName="FylkesId" displayText="Fylke" />
                            </div>
                        </li>
                        <li class="mb-1">
                            Oppdatert:
                            <strong v-text="updated"></strong>
                        </li>
                    </ul>
                </div>
            </div>
            <!-- // Meta -->
            <!-- Main -->
            <div class="container bg-white md:flex">
                <div class="w-full md:w-3/4 p-4">
                    <!-- Tabs -->
                    <ul class="flex flex-wrap -m-4 mb-4 divide-x-2 divide-white">
                        <li v-for="tab in tabs" :key="tab.id" class="w-full md:flex-1 md:w-auto text-center">
                            <button @click="changeTab(tab.id)" class="w-full p-4 hover:text-gray-800 focus:outline-none" :class="[selectedTabId === tab.id ? 'bg-white text-gray-800' : 'bg-gray-100 text-gray-500']">
                                {{ tab.title }}
                            </button>
                        </li>
                    </ul>
                    <!-- // Tabs -->
                    <!-- Content tab 1-->
                    <div v-if="selectedTabId === 1" class="w-full text-sm">
                        <template v-if="model.temabeskrivelse || inEditMode">
                            <h2 class="text-green-700 mb-1 text-xl">
                                Konkretisering av område/tema:
                            </h2>
                            <p v-epi-edit="'Temabeskrivelse'" v-html="model.temabeskrivelse" class="leading-1 article mb-8"></p>
                        </template>

                        <template v-if="model.temautfordring || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                Utfordringer ved valg av tema:
                            </h2>
                            <p v-epi-edit="'Temautfordring'" v-html="model.temautfordring" class="leading-1 article mb-8"></p>
                        </template>


                        <template v-if="model.aktiviteter || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                Aktiviteter i tiltaket:
                            </h2>
                            <p v-epi-edit="'Aktiviteter'" v-html="model.aktiviteter" class="leading-1 article mb-8"></p>
                        </template>
                    </div>
                    <!-- // Content tab 1 -->
                    <!-- Content tab 2 -->
                    <div v-if="selectedTabId === 2" class="w-full text-sm">
                        <template v-if="model.malsetting || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                Målsetting for tiltak
                            </h2>
                            <p v-epi-edit="'Malsetting'" v-html="model.malsetting" class="leading-1 article mb-8"></p>
                        </template>

                        <template v-if="model.forskningGrunn || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                Forskningsmessig begrunnelse for valg av tiltak:
                            </h2>
                            <p v-epi-edit="'ForskningGrunn'" v-html="model.forskningGrunn" class="leading-1 article mb-8"></p>
                        </template>


                        <template v-if="model.referanser || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                Referanser til forskning/faglitteratur:
                            </h2>
                            <p v-epi-edit="'Referanser'" v-html="model.referanser" class="leading-1 article mb-8"></p>
                        </template>
                    </div>
                    <!-- // Content tab 2 -->
                    <!-- Content tab 3 -->
                    <div v-if="selectedTabId === 3" class="w-full text-sm">
                        <template v-if="model.budsjett || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                Budsjett/kostnader:
                            </h2>
                            <p v-epi-edit="'Budsjett'" v-html="model.budsjett" class="leading-1 article mb-8"></p>
                        </template>
                    </div>
                    <!-- // Content tab 3 -->
                    <!-- Content tab 4 -->
                    <div v-if="selectedTabId === 4" class="w-full text-sm">

                        <template v-if="model.hvordanBidra || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                Hvordan har arbeidet bidratt til å integrere psykisk helse og/ eller rusmiddelforebygging i det videre systematiske folkehelsearbeidet?
                            </h2>
                            <p v-epi-edit="'HvordanBidra'" v-html="model.hvordanBidra" class="leading-1 article mb-8"></p>
                        </template>

                        <template v-if="model.resultater || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                Resultater
                            </h2>
                            <p v-epi-edit="'Resultater'" v-html="model.resultater" class="leading-1 article mb-8"></p>
                        </template>

                        <template v-if="model.verktoy || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                Verktøy
                            </h2>
                            <p v-epi-edit="'Verktoy'" v-html="model.verktoy" class="leading-1 article mb-8"></p>
                        </template>

                        <template v-if="model.leringspunkter || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                Læringspunkter
                            </h2>
                            <p v-epi-edit="'Leringspunkter'" v-html="model.leringspunkter" class="leading-1 article mb-8"></p>
                        </template>

                        <template v-if="model.innovasjonsarbeid || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                Innovasjonsarbeid
                            </h2>
                            <p v-epi-edit="'Innovasjonsarbeid'" v-html="model.innovasjonsarbeid" class="leading-1 article mb-8"></p>
                        </template>

                        <template v-if="model.evalHvordan || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                Hvordan skal tiltaket evalueres?
                            </h2>
                            <p v-epi-edit="'EvalHvordan'" v-html="model.evalHvordan" class="leading-1 article mb-8"></p>
                        </template>

                        <template v-if="model.evalGrunnValg || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                Hva er begrunnelsen for valg av type evaluering?
                            </h2>
                            <p v-epi-edit="'EvalGrunnValg'" v-html="model.evalGrunnValg" class="leading-1 article mb-8"></p>
                        </template>

                        <template v-if="model.evalProblem || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                Hvilke problemstillinger er sentrale i evalueringen?
                            </h2>
                            <p v-epi-edit="'EvalProblem'" v-html="model.evalProblem" class="leading-1 article mb-8"></p>
                        </template>

                        <template v-if="model.hvaSlagsData || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                Hva slags data skal samles inn?
                            </h2>
                            <p v-epi-edit="'HvaSlagsData'" v-html="model.hvaSlagsData" class="leading-1 article mb-8"></p>
                        </template>

                        <template v-if="model.evalTidsplan || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                Hva er tidsplanen for evaluering?
                            </h2>
                            <p v-epi-edit="'EvalTidsplan'" v-html="model.evalTidsplan" class="leading-1 article mb-8"></p>
                        </template>

                        <template v-if="model.evalHvemGjor || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                Hvem samarbeider dere med om evalueringen?
                            </h2>
                            <p v-epi-edit="'EvalHvemGjor'" v-html="model.evalHvemGjor" class="leading-1 article mb-8"></p>
                        </template>

                        <template v-if="model.evalPublisering || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                Er det tenkt publisering av artikler fra evalueringen?
                            </h2>
                            <p v-epi-edit="'EvalPublisering'" v-html="model.evalPublisering" class="leading-1 article mb-8"></p>
                        </template>

                        <template v-if="model.videreforing || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                Videreføring etter tiltaksutviklingen:
                            </h2>
                            <div v-epi-edit="'Videreforing'" v-html="model.videreforing" class="leading-1 article mb-8"></div>
                        </template>

                        <template v-if="model.nettomtale || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                Finnes det nettomtale av tiltaket?
                            </h2>
                            <div v-epi-edit="'Nettomtale'" v-html="model.nettomtale" class="leading-1 article mb-8"></div>
                        </template>

                        <template v-if="model.annet || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                Er det noe annet du har lyst til å dele fra prosjektet?
                            </h2>
                            <div v-epi-edit="'Annet'" v-html="model.annet" class="leading-1 article mb-8"></div>
                        </template>
                    </div>
                    <!-- // Content tab 4 -->
                    <!-- Content tab 5 -->
                    <div v-if="selectedTabId === 5" class="w-full text-sm">
                        <template v-if="model.kontaktNavn || inEditMode">
                            <h2 class="text-green-700 mb-4 text-lg">
                                Kontaktpersoner:
                            </h2>
                            <!--<p  v-epi-edit="'KontaktNavn'"  v-html="model.kontaktNavn" class="leading-1 article mb-8"></p>-->
                        </template>

                        <ul>
                            <li v-for="kontakt in model.kontakter" :key="kontakt.name" class="mb-2">
                                <template v-if="kontakt.name">
                                    Navn:
                                    <strong class="pl-2">
                                        {{ kontakt.name }}
                                    </strong>
                                </template>
                                <br />
                                <template v-if="kontakt.email">
                                    E-post:
                                    <strong class="pl-2">
                                        {{ kontakt.email }}
                                    </strong>
                                </template>
                                <br />
                                <template v-if="kontakt.phone">
                                    Telefon:
                                    <strong class="pl-2">
                                        {{ kontakt.phone }}
                                    </strong>
                                </template>
                                <br />
                                <template v-if="kontakt.role">
                                    Kommunalsjefsområde:
                                    <strong class="pl-2">
                                        {{ kontakt.role }}
                                    </strong>
                                </template>
                            </li>
                        </ul>

                        <!--<template v-if="model.kontaktEpost || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                E-post:
                            </h2>
                            <p v-epi-edit="'KontaktEpost'"  v-html="model.kontaktEpost" class="leading-1 article mb-8"></p>
                        </template>

                        <template v-if="model.kontaktTlf || inEditMode">
                            <h2 class="text-green-700 mb-1 text-lg">
                                Telefon:
                            </h2>
                            <p v-epi-edit="'KontaktTlf'"  v-html="model.kontaktTlf" class="leading-1 article mb-8"></p>
                        </template>-->
                    </div>
                    <!-- // Content tab 5 -->
                </div>

                <div class="w-full md:w-1/4 border-l text-center pl-4">
                    <div class="flex flex-row my-4">
                        <a :href="tiltakCountyPageUrl" title="Til fylkesoversikt" target="_self" class="flex-1 flex items-center justify-center border p-4 m-2 btn btn-green btn-xs">
                            Tilbake til fylkesoversikt
                        </a>
                        <a :href="tiltakStartPageUrl" title="Til startsiden" target="_self" class="flex-1 flex items-center justify-center border p-4 m-2 btn btn-green btn-xs">
                            Tilbake til startsiden
                        </a>
                    </div>
                    <img :src="model.fylkeskartImage"
                         alt="Kart over Norge med utheved felt for fylket"
                         class="inline-block" />
                </div>
            </div>
            <!-- // Main -->
        </Container>
    </DefaultTemplate>
</template>

<script>
import Breadcrumbs from '@/Scripts/components/organisms/Breadcrumbs/breadcrumbs.vue';
import Container from '@/Scripts/components/atoms/Container/index.vue';
import ContentArea from '@/Scripts/components/ContentArea.vue';
import EpiProperty from '@/Scripts/components/EpiProperty.vue';
import { mapState } from 'vuex';

export default {
    props: ['model'],
    data() {
        console.log(this.model);
        return {
            tiltakStartPageUrl: this.model.tiltakStartPage,
            tiltakCountyPageUrl: this.model.tiltakStartPage + '?tab=1&county=' + this.model.fylkesId,
            arenas: [],
            targetgroups: [],
            themes: [],
            tabs: [
                {
                    id: 1,
                    title: 'Beskrivelse'
                },
                {
                    id: 2,
                    title: 'Målsetting/bakgrunn'
                },
                {
                    id: 3,
                    title: 'Budsjett/kostnader'
                },
                {
                    id: 4,
                    title: 'Evaluering/videreføring'
                },
                {
                    id: 5,
                    title: 'Kontaktpersoner'
                }
            ],
            selectedTabId: 1,
            updated: ('0' + new Date(this.model.changed).getDate()).slice(-2) + '.' + ('0' + (new Date(this.model.changed).getMonth() + 1)).slice(-2) + '.' + new Date(this.model.changed).getFullYear()
        };
    },
    components: {
        Breadcrumbs,
        ContentArea,
        Container,
        EpiProperty
    },
    computed: mapState({
        inEditMode: state => state.epiContext.inEditMode
    }),

    methods: {
        getArenas() {
            this.model.arenaer.filter(arena => {
                if (this.model.arenaIdLst.includes(arena.id)) {
                    this.arenas.push(arena.navn);
                }
            });
        },
        getTargetgroups() {
            this.model.maalgrupper.filter(targetgroup => {
                if (this.model.malgruppeIdLst.includes(targetgroup.id)) {
                    this.targetgroups.push(targetgroup.navn);
                }
            });
        },
        getThemes() {
            this.model.temaer.filter(theme => {
                if (this.model.temaIdLst.includes(theme.id)) {
                    this.themes.push(theme.navn);
                }
            });
        },
        changeTab(id) {
            this.selectedTabId = id;
        }
    },
    mounted() {
        this.getArenas();
        this.getTargetgroups();
        this.getThemes();
    }
};
</script>
